import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { cn } from '../../lib/utils';
import {
  CheckCircle2, Upload, AlertTriangle, FileText, Users, Calendar,
  BarChart3, CreditCard, Hash, MapPin, Table2, Info, ArrowRight,
  Search, Loader2, Stethoscope, Building2, Mail, Plus, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import SetupUploadLightbox from './SetupUploadLightbox';
import { guardWrite } from '../../lib/modeGuard';
import { useAppMode } from '../../contexts/AppModeContext';

const APP_DOCS = {
  lumina: [
    { id: 'ccda', label: 'CCDA / CCD Export', desc: 'Continuity of Care Documents from your existing EHR', icon: FileText },
    { id: 'patient_csv', label: 'Patient List (.csv)', desc: 'CSV with patient names, DOB, insurance, and contact info', icon: Users },
    { id: 'appt_list', label: 'Appointment Schedule', desc: 'Daily or weekly appointment list from your EHR', icon: Calendar },
  ],
  revq: [
    { id: 'edi_837', label: 'EDI 837 Claim Batch', desc: 'Standard electronic claim file from your billing software', icon: BarChart3, required: true },
    { id: 'edi_835', label: 'EDI 835 Remittance (ERA)', desc: 'Electronic Remittance Advice for payment reconciliation', icon: CreditCard },
    { id: 'patient_balances', label: 'Patient Balances Report', desc: 'Aging report or patient balance export', icon: FileText },
    { id: 'fee_schedule', label: 'Fee Schedule', desc: 'Contracted fee schedule for claims scrubbing', icon: Hash },
    { id: 'csv_insurance', label: 'Patient Insurance List (.csv)', desc: 'Member IDs, payer names, copays', icon: Table2 },
    { id: 'csv_demographics', label: 'Patient Demographics (.csv)', desc: 'Names, DOBs, addresses, phone numbers', icon: Users },
    { id: 'csv_practice_report', label: 'Practice Report (.csv)', desc: 'Revenue summaries, encounter volumes', icon: BarChart3 },
    { id: 'csv_physician', label: 'Physician Details (.csv)', desc: 'Provider names, NPIs, specialties, tax IDs', icon: FileText },
    { id: 'csv_service_location', label: 'Service Location (.csv)', desc: 'Facility names, addresses, POS codes', icon: MapPin },
  ],
  echo: [
    { id: 'appt_list_echo', label: 'Appointment Schedule', desc: 'Daily or weekly appointment list', icon: Calendar },
    { id: 'calendar_export', label: 'Calendar Export (.ics / .csv)', desc: 'Provider calendars with availability', icon: Calendar },
    { id: 'patient_csv_echo', label: 'Patient Roster (.csv)', desc: 'Demographics roster for patient management', icon: Users },
  ],
};

const AccountSetupTab = ({ appKey }) => {
  const { isDemo } = useAppMode();
  const [uploadedFiles, setUploadedFiles] = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem('ariaone-setup-progress') || '{}'); return (saved.uploadedFiles || []).reduce((acc, id) => ({ ...acc, [id]: true }), {}); } catch { return {}; }
  });
  const [uploadLightbox, setUploadLightbox] = useState(null);

  const docs = APP_DOCS[appKey] || [];
  const uploadedCount = docs.filter(d => uploadedFiles[d.id]).length;
  const progress = docs.length > 0 ? Math.round((uploadedCount / docs.length) * 100) : 100;

  const handleFileUploaded = (docId) => {
    setUploadedFiles(prev => {
      const next = { ...prev, [docId]: true };
      try {
        const saved = JSON.parse(localStorage.getItem('ariaone-setup-progress') || '{}');
        saved.uploadedFiles = [...new Set([...(saved.uploadedFiles || []), docId])];
        saved.progress = Math.round(([...new Set(saved.uploadedFiles)].length / docs.length) * 100);
        localStorage.setItem('ariaone-setup-progress', JSON.stringify(saved));
      } catch {}
      return next;
    });
    toast.success('Document uploaded successfully');
  };

  // Team member setup state
  const [teamMembers, setTeamMembers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ariaone-setup-team') || '[]'); } catch { return []; }
  });
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('');
  const [memberCategory, setMemberCategory] = useState('medical');
  const [memberNpi, setMemberNpi] = useState('');
  const [npiLoading, setNpiLoading] = useState(false);
  const [npiVerified, setNpiVerified] = useState(false);
  const [npiResult, setNpiResult] = useState(null);

  const SETUP_ROLES = {
    medical: [
      { id: 'physician', label: 'Physician (MD/DO)' },
      { id: 'np', label: 'Nurse Practitioner' },
      { id: 'pa', label: 'Physician Assistant' },
      { id: 'rn', label: 'Registered Nurse' },
      { id: 'ma', label: 'Medical Assistant' },
    ],
    nonMedical: [
      { id: 'practice_admin', label: 'Practice Administrator' },
      { id: 'office_manager', label: 'Office Manager' },
      { id: 'front_desk', label: 'Front Desk' },
      { id: 'biller', label: 'Biller / Coder' },
      { id: 'scheduler', label: 'Scheduling Coordinator' },
    ],
  };

  const NPI_MOCK_DB = {
    '1234567890': { name: 'Dr. Michael Chen', specialty: 'Internal Medicine', address: '100 Medical Pkwy, Austin TX 78701' },
    '9876543210': { name: 'Dr. Sarah Wilson', specialty: 'Family Medicine', address: '100 Medical Pkwy, Austin TX 78701' },
    '5551234567': { name: 'Dr. James Roberts', specialty: 'Pediatrics', address: '100 Medical Pkwy, Austin TX 78701' },
    '1112223334': { name: 'Dr. Patricia Adams', specialty: 'Endocrinology', address: '450 Cedar Blvd, Austin TX 78745' },
    '4445556667': { name: 'Dr. Kevin Park', specialty: 'Cardiology', address: '800 S Congress Ave, Austin TX 78704' },
  };

  const handleNpiLookup = () => {
    if (!memberNpi || memberNpi.length < 10) { toast.error('Enter a valid 10-digit NPI number'); return; }
    setNpiLoading(true);
    setNpiVerified(false);
    setNpiResult(null);
    setTimeout(() => {
      const found = NPI_MOCK_DB[memberNpi];
      setNpiLoading(false);
      if (found) {
        setNpiVerified(true);
        setNpiResult(found);
        setMemberName(found.name);
        setMemberRole('physician');
        setMemberCategory('medical');
        toast.success('NPI verified', { description: `${found.name} — ${found.specialty} — ${found.address}` });
      } else {
        setNpiResult({ name: null, specialty: null, address: null, notFound: true });
        toast.info('NPI not found in registry', { description: 'You can still enter details manually' });
      }
    }, 1200);
  };

  const handleAddTeamMember = () => {
    if (!memberName || !memberEmail || !memberRole) { toast.error('Fill in all required fields'); return; }
    guardWrite('Add team member', () => {
      const newMember = { name: memberName, email: memberEmail, role: memberRole, category: memberCategory, npi: memberNpi || null, specialty: npiResult?.specialty || null };
      const updated = [...teamMembers, newMember];
      setTeamMembers(updated);
      try { localStorage.setItem('ariaone-setup-team', JSON.stringify(updated)); } catch {}
      toast.success(`${memberName} added to team`);
      setMemberName(''); setMemberEmail(''); setMemberRole(''); setMemberNpi('');
      setNpiVerified(false); setNpiResult(null); setMemberCategory('medical');
      setAddMemberOpen(false);
    }, isDemo);
  };

  const removeTeamMember = (idx) => {
    guardWrite('Remove team member', () => {
      const updated = teamMembers.filter((_, i) => i !== idx);
      setTeamMembers(updated);
      try { localStorage.setItem('ariaone-setup-team', JSON.stringify(updated)); } catch {}
      toast.success('Member removed');
    }, isDemo);
  };

  const getRoleLabel = (roleId) => {
    const all = [...SETUP_ROLES.medical, ...SETUP_ROLES.nonMedical];
    return all.find(r => r.id === roleId)?.label || roleId;
  };

  return (
    <div className="space-y-4">
      {/* Progress */}
      <Card data-testid="account-setup-progress">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Account Setup</p>
              <p className="text-xs text-slate-500 mt-0.5">{uploadedCount} of {docs.length} documents uploaded · {teamMembers.length} team members</p>
            </div>
            <span className={cn("text-sm font-bold", progress === 100 && teamMembers.length > 0 ? "text-emerald-600" : "text-slate-800")}>{progress}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full transition-all duration-500", progress === 100 && teamMembers.length > 0 ? "bg-emerald-500" : "bg-slate-900")} style={{ width: `${progress}%` }} />
          </div>
          {progress < 100 && (
            <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-700">Account setup is incomplete. Upload remaining documents and add team members to unlock full functionality.</p>
            </div>
          )}
          {progress === 100 && teamMembers.length > 0 && (
            <div className="flex items-center gap-2 mt-3 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              <p className="text-[11px] text-emerald-700 font-medium">Setup complete! All documents uploaded and team configured.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ TEAM SETUP SECTION ═══ */}
      <Card data-testid="account-setup-team">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Team Setup</CardTitle>
              <p className="text-xs text-slate-400 mt-0.5">Add providers and staff with NPI verification</p>
            </div>
            <Button size="sm" className="text-xs gap-1.5 bg-[#111] hover:bg-[#222] h-8" onClick={() => setAddMemberOpen(true)} data-testid="setup-add-member-btn">
              <Plus className="h-3 w-3" /> Add Team Member
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {teamMembers.length === 0 && (
            <div className="text-center py-6">
              <Users className="h-8 w-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No team members added yet</p>
              <p className="text-[10px] text-slate-300 mt-0.5">Add providers with NPI lookup for automatic verification</p>
            </div>
          )}
          {teamMembers.map((m, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100" data-testid={`setup-member-${i}`}>
              <div className={cn("h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0", m.category === 'medical' ? 'bg-blue-600' : 'bg-slate-600')}>
                {m.name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-800">{m.name}</p>
                <p className="text-[10px] text-slate-400">{m.email}</p>
              </div>
              <Badge variant="outline" className="text-[8px] shrink-0">{getRoleLabel(m.role)}</Badge>
              {m.npi && <span className="text-[8px] text-slate-400 shrink-0">NPI: {m.npi}</span>}
              {m.specialty && <Badge className="text-[7px] border-0 bg-blue-50 text-blue-600 shrink-0">{m.specialty}</Badge>}
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500 shrink-0" onClick={() => removeTeamMember(i)}>Remove</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Document list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Documents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {docs.map(doc => {
            const Icon = doc.icon;
            const isUploaded = uploadedFiles[doc.id];
            return (
              <div key={doc.id} className={cn("flex items-center gap-3 p-3 rounded-lg border transition-all", isUploaded ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200")} data-testid={`setup-doc-${doc.id}`}>
                <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0", isUploaded ? "bg-emerald-100" : "bg-slate-100")}>
                  {isUploaded ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Icon className="h-4 w-4 text-slate-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-800">{doc.label}</p>
                    {doc.required && <Badge className="text-[8px] bg-red-50 text-red-600 border-0">Required</Badge>}
                  </div>
                  <p className="text-xs text-slate-400">{doc.desc}</p>
                </div>
                {isUploaded ? (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border-0">Uploaded</Badge>
                ) : (
                  <Button variant="outline" size="sm" className="h-7 text-[11px] gap-1 border-slate-200"
                    onClick={() => setUploadLightbox({ docId: doc.id, label: doc.label })} data-testid={`settings-upload-${doc.id}`}>
                    <Upload className="h-3 w-3" /> Upload
                  </Button>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <SetupUploadLightbox
        open={!!uploadLightbox}
        onOpenChange={v => { if (!v) setUploadLightbox(null); }}
        docId={uploadLightbox?.docId}
        docLabel={uploadLightbox?.label}
        onFileSelected={() => handleFileUploaded(uploadLightbox?.docId)}
      />

      {/* ═══ ADD TEAM MEMBER DIALOG WITH NPI LOOKUP ═══ */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="setup-add-member-dialog">
          <DialogHeader className="px-6 pt-5 pb-3">
            <DialogTitle className="text-base font-semibold">Add Team Member</DialogTitle>
            <DialogDescription className="text-xs">Add a provider or staff member. Use NPI lookup to auto-fill physician details.</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-5 space-y-3 max-h-[60vh] overflow-y-auto">
            {/* NPI Lookup */}
            <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100" data-testid="setup-npi-section">
              <p className="text-[10px] font-semibold text-blue-700 mb-1.5">NPI Lookup (for physicians)</p>
              <div className="flex gap-2">
                <input type="text" placeholder="Enter 10-digit NPI number..." value={memberNpi}
                  onChange={e => { setMemberNpi(e.target.value.replace(/\D/g, '').slice(0, 10)); setNpiVerified(false); setNpiResult(null); }}
                  className="flex-1 px-3 py-2 rounded-lg border border-blue-200 text-xs outline-none focus:border-blue-400 bg-white font-mono"
                  data-testid="setup-npi-input" />
                <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700 gap-1 h-9 shrink-0" onClick={handleNpiLookup} disabled={npiLoading || memberNpi.length < 10} data-testid="setup-npi-lookup-btn">
                  {npiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                  {npiLoading ? 'Looking up...' : 'Lookup NPI'}
                </Button>
              </div>
              {npiVerified && npiResult && !npiResult.notFound && (
                <div className="mt-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="setup-npi-result">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <span className="text-xs font-semibold text-emerald-700">NPI Verified</span>
                  </div>
                  <div className="mt-1 text-[10px] text-emerald-600 space-y-0.5">
                    <p><span className="text-emerald-500">Name:</span> {npiResult.name}</p>
                    <p><span className="text-emerald-500">Specialty:</span> {npiResult.specialty}</p>
                    <p><span className="text-emerald-500">Address:</span> {npiResult.address}</p>
                  </div>
                </div>
              )}
              {npiResult?.notFound && (
                <div className="mt-2 flex items-center gap-2 text-[10px] text-amber-600">
                  <AlertTriangle className="h-3 w-3" /> NPI not found — enter details manually below
                </div>
              )}
            </div>

            {/* Category */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">Account Type</p>
              <div className="flex gap-2">
                {[{ id: 'medical', label: 'Medical', icon: Stethoscope }, { id: 'nonMedical', label: 'Non-Medical', icon: Building2 }].map(c => (
                  <button key={c.id} onClick={() => { setMemberCategory(c.id); setMemberRole(''); }}
                    className={cn("flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors",
                      memberCategory === c.id ? "bg-[#111] text-white" : "bg-slate-100 text-slate-600"
                    )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`setup-cat-${c.id}`}>
                    <c.icon className="h-3 w-3" /> {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name & Email */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Full Name *</p>
                <input type="text" value={memberName} onChange={e => setMemberName(e.target.value)} placeholder="Dr. Jane Doe"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400" data-testid="setup-member-name" />
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Email *</p>
                <input type="email" value={memberEmail} onChange={e => setMemberEmail(e.target.value)} placeholder="jane@clinic.com"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400" data-testid="setup-member-email" />
              </div>
            </div>

            {/* Role */}
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1.5">Role *</p>
              <div className="flex flex-wrap gap-1">
                {(memberCategory === 'medical' ? SETUP_ROLES.medical : SETUP_ROLES.nonMedical).map(r => (
                  <button key={r.id} onClick={() => setMemberRole(r.id)}
                    className={cn("px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors",
                      memberRole === r.id ? "bg-[#111] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    )} style={{ border: 'none', cursor: 'pointer' }} data-testid={`setup-role-${r.id}`}>{r.label}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-slate-100 gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setAddMemberOpen(false)}>Cancel</Button>
            <Button size="sm" className="text-xs bg-[#111] hover:bg-[#222] gap-1" onClick={handleAddTeamMember} data-testid="setup-confirm-member">
              <Mail className="h-3 w-3" /> Add & Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountSetupTab;
